import React, { useEffect, useState, useCallback } from "react";
import { api, todayISO, fmtDate, formatApiError } from "../lib/api";
import { CheckCircle2, Circle, Plus, Trash2, RefreshCw, ClipboardList, Edit3, X, Save } from "lucide-react";

function PageHeader() {
  return (
    <div className="mb-6">
      <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1 flex items-center gap-1.5">
        <ClipboardList size={11} /> Daily Ops
      </div>
      <h1 className="font-black text-3xl tracking-tight">Daily Checklist</h1>
      <p className="text-sm text-gray-500 mt-1">Track daily operational tasks — visible to the whole team</p>
    </div>
  );
}

export function ChecklistPage() {
  const [date, setDate]         = useState(todayISO());
  const [dayData, setDayData]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [addTitle, setAddTitle] = useState("");
  const [addAssigned, setAddAssigned] = useState("All");
  const [showAdd, setShowAdd]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/checklist/day?date=${date}`);
      setDayData(r.data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (itemId, currentDone) => {
    try {
      await api.post(`/checklist/items/${itemId}/toggle`, { date, done: !currentDone });
      setDayData(prev => ({
        ...prev,
        items: prev.items.map(it => it.id === itemId
          ? { ...it, done: !currentDone, done_by: !currentDone ? "You" : "" }
          : it),
        done_count: prev.done_count + (!currentDone ? 1 : -1),
      }));
    } catch (err) { setError(formatApiError(err)); }
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!addTitle.trim()) return;
    setSaving(true);
    try {
      await api.post("/checklist/items", { title: addTitle.trim(), assigned_to: addAssigned, order: 999 });
      setAddTitle(""); setShowAdd(false);
      await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm("Delete this checklist item?")) return;
    try { await api.delete(`/checklist/items/${itemId}`); await load(); }
    catch (err) { setError(formatApiError(err)); }
  };

  const saveEdit = async (itemId) => {
    try {
      await api.patch(`/checklist/items/${itemId}`, editDraft);
      setEditingId(null);
      await load();
    } catch (err) { setError(formatApiError(err)); }
  };

  const pct = dayData ? Math.round((dayData.done_count / Math.max(1, dayData.total)) * 100) : 0;

  return (
    <div className="px-6 py-6 max-w-2xl" data-testid="checklist-page">
      <PageHeader />
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex justify-between">
          <span>{error}</span><button onClick={() => setError("")} className="font-bold ml-4">×</button>
        </div>
      )}

      {/* Date picker + refresh */}
      <div className="flex items-center gap-3 mb-6">
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="checklist-date" />
        <button onClick={load} className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
        <div className="text-sm text-gray-500 ml-auto">{fmtDate(date)}</div>
      </div>

      {/* Progress bar */}
      {dayData && (
        <div className="mb-5 bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">
              {dayData.done_count} / {dayData.total} tasks done
            </span>
            <span className={`text-sm font-bold ${pct === 100 ? "text-green-600" : "text-gray-700"}`}>{pct}%</span>
          </div>
          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${pct === 100 ? "bg-green-500" : "bg-gray-900"}`}
              style={{ width: `${pct}%` }} />
          </div>
          {pct === 100 && (
            <div className="text-xs text-green-600 font-semibold mt-2 text-center">All tasks complete for today!</div>
          )}
        </div>
      )}

      {/* Items list */}
      {loading ? (
        <div className="py-10 text-center text-sm text-gray-400">Loading checklist…</div>
      ) : (
        <div className="space-y-2">
          {(dayData?.items || []).map(item => (
            <div key={item.id}
              className={`bg-white border rounded-xl overflow-hidden transition-all ${item.done ? "border-green-200 bg-green-50/30" : "border-gray-200"}`}
              data-testid={`checklist-item-${item.id}`}>
              {editingId === item.id ? (
                <div className="p-4 space-y-3">
                  <input value={editDraft.title} onChange={e => setEditDraft(d => ({...d, title: e.target.value}))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
                  <input value={editDraft.assigned_to} onChange={e => setEditDraft(d => ({...d, assigned_to: e.target.value}))}
                    placeholder="Assigned to (e.g. All, Driver, Manager)"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(item.id)} className="flex items-center gap-1 bg-gray-900 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                      <Save size={11} /> Save
                    </button>
                    <button onClick={() => setEditingId(null)} className="text-xs text-gray-500 hover:text-gray-700 px-2">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-4">
                  <button onClick={() => toggle(item.id, item.done)} className="flex-shrink-0" data-testid={`toggle-item-${item.id}`}>
                    {item.done
                      ? <CheckCircle2 size={22} className="text-green-500" />
                      : <Circle size={22} className="text-gray-300 hover:text-gray-500" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold ${item.done ? "line-through text-gray-400" : "text-gray-800"}`}>
                      {item.title}
                    </div>
                    {(item.assigned_to || item.done_by) && (
                      <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-2">
                        {item.assigned_to && <span>→ {item.assigned_to}</span>}
                        {item.done && item.done_by && <span className="text-green-600">✓ {item.done_by}</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => { setEditingId(item.id); setEditDraft({ title: item.title, assigned_to: item.assigned_to || "", order: item.order || 0 }); }}
                      className="p-1.5 text-gray-300 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                      <Edit3 size={13} />
                    </button>
                    <button onClick={() => deleteItem(item.id)} className="p-1.5 text-gray-300 hover:text-red-500 rounded-lg hover:bg-red-50">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {(dayData?.items || []).length === 0 && (
            <div className="py-10 text-center bg-white border border-gray-200 rounded-xl">
              <ClipboardList size={28} className="mx-auto mb-3 text-gray-200" />
              <div className="text-sm text-gray-500">No checklist items yet</div>
              <div className="text-xs text-gray-400 mt-1">Add items below to track daily tasks</div>
            </div>
          )}
        </div>
      )}

      {/* Add item form */}
      <div className="mt-4">
        {!showAdd ? (
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 font-semibold px-3 py-2 rounded-lg border border-dashed border-gray-300 hover:border-gray-500 transition-colors"
            data-testid="add-checklist-btn">
            <Plus size={14} /> Add Checklist Item
          </button>
        ) : (
          <form onSubmit={addItem} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Task Title *</label>
              <input autoFocus value={addTitle} onChange={e => setAddTitle(e.target.value)} required placeholder="e.g. Check godown stock levels"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" data-testid="checklist-title-input" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Assigned To</label>
              <input value={addAssigned} onChange={e => setAddAssigned(e.target.value)} placeholder="All / Driver / Manager…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={saving} className="bg-gray-900 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50" data-testid="save-checklist-btn">
                {saving ? "Saving…" : "Add Task"}
              </button>
              <button type="button" onClick={() => setShowAdd(false)} className="text-sm text-gray-500 hover:text-gray-700 px-3">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ChecklistPage;
